import { useMemo, useState } from 'react'
import Button from '../ui/elements/button'
import ModalOverlay, {
  ModalOverlayContent,
  ModalOverlayFooter,
} from '../ui/shared/overlays/modal-overlay'
import bandOrders from '../../band-orders'
import Select from '../ui/elements/select'
import ValidationMessage from '../ui/shared/validation-message'
import InfoText from '../ui/shared/info-text'

type BandsModal = {
  numberBands: number
  bands: string
  show: boolean
  onSave: (bands: string) => void
  onDismiss: () => void
}

const bandOptions: [string, string, string][] = [
  [' ', 'Not mapped', 'tw-text-system-grey-200 tw-bg-system-grey-100'],
  ['r', 'Red', 'tw-text-danger-200 tw-bg-danger-100'],
  ['g', 'Green', 'tw-text-success-300 tw-bg-success-100'],
  ['b', 'Blue', 'tw-text-primary-300 tw-bg-primary-100'],
  ['n', 'NIR', 'tw-text-system-grey-400 tw-bg-system-grey-100'],
  ['e', 'Red edge', 'tw-text-system-grey-400 tw-bg-system-grey-100'],
  ['t', 'Thermal', 'tw-text-system-grey-400 tw-bg-system-grey-100'],
  ['a', 'Alpha', 'tw-text-system-grey-400 tw-bg-white'],
]

export default function BandsModal({
  numberBands,
  bands: initialBands,
  show,
  onSave,
  onDismiss,
}: BandsModal) {
  const [bands, setBands] = useState(initialBands)
  const [valid, configurationName] = useBandsConfiguration(
    bands,
    numberBands,
    false
  )

  return (
    <ModalOverlay title="Bands" show={show} onDismiss={onDismiss}>
      <ModalOverlayContent>
        <div className="tw-mb-4">
          <InfoText>
            Please choose band configuration for the Orthomosaic
          </InfoText>
        </div>
        <div className="tw-grid tw-grid-cols-1 tw-gap-x-4">
          {Array.from({ length: numberBands }).map((_, i) => (
            <div
              key={i}
              className="tw-flex tw-flex-row tw-my-2 tw-gap-x-2 tw-items-center">
              <div className="tw-custom-uppercase tw-text-system-grey-400">
                Band&nbsp;{i + 1}
              </div>
              <Select
                value={bands[i]}
                onChange={e => {
                  const newValue = e.target.value
                  const nextBands = Array.from(bands)
                  const currentIndex = bands.indexOf(newValue)
                  if (currentIndex >= 0) {
                    nextBands[currentIndex] = nextBands[i]
                  }
                  nextBands[i] = newValue
                  setBands(nextBands.join(''))
                }}>
                {bandOptions.map(([id, name, className]) => (
                  <option key={id} value={id} className={className}>
                    {name}
                  </option>
                ))}
              </Select>
            </div>
          ))}
        </div>
        <div className="tw-mt-4">
          {valid ? (
            <>
              <div className="tw-text-system-grey-400 tw-custom-uppercase">
                Current configuration
              </div>
              <div>{configurationName || 'Custom configuration'}</div>
            </>
          ) : (
            <ValidationMessage>Please map all bands</ValidationMessage>
          )}
        </div>
      </ModalOverlayContent>
      <ModalOverlayFooter>
        <div className="tw-flex tw-flex-row tw-justify-between tw-gap-x-4">
          <Button
            size="large"
            action="primary"
            disabled={!valid}
            onClick={() => onSave(bands)}>
            Save
          </Button>
          <Button size="large" action="default" onClick={onDismiss}>
            Cancel
          </Button>
        </div>
      </ModalOverlayFooter>
    </ModalOverlay>
  )
}

export function useBandsConfiguration(
  bands: string | undefined,
  numberBands: number | undefined,
  short: boolean
) {
  return useMemo(() => {
    if (
      !bands ||
      numberBands == null ||
      bands?.length !== numberBands ||
      bands?.includes(' ')
    )
      return [false, 'Incomplete band configuration']

    const joinedBands = [...bands]
      .map(
        b =>
          bandOptions.find(
            ([bandAbbreviation]) => bandAbbreviation === b
          )?.[1] || b
      )
      .join(', ')

    if (!short || joinedBands.length < 40) {
      return [true, joinedBands]
    } else {
      return [true, bands]
    }
  }, [bands, numberBands, short])
}
